<template>
  <div class="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <Accordion>
      <AccordionItem :defaultOpen="true">
        <template #accordion-trigger>
          <div class="flex items-center gap-2 py-2 text-gray-700">
            <h2 class="text-lg font-semibold">Filtros de comprobantes</h2>
          </div>
        </template>
        <template #accordion-content>
          <li class="py-2">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div class="min-w-0">
                <div class="text-sm font-bold text-gray-500">Nro. comprobante</div>
                <input
                  class="block h-10 w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 sm:text-sm"
                  type="text"
                  v-model="nroComprobante"
                  placeholder="Buscar por nro. de comprobante..."
                  @keydown.enter.prevent="filtrar"
                />
              </div>
              <div class="min-w-0">
                <div class="text-sm font-bold text-gray-500">Fecha</div>
                <select
                  class="block h-10 w-full appearance-none truncate rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 sm:text-sm"
                  type="text"
                  @change="onFechaFilterChange"
                  v-model="fechaFilter"
                >
                  <option value="all">Todos los comprobantes</option>
                  <option value="last-week">Última semana</option>
                  <option value="last-month">Último mes</option>
                  <option value="range">Rango específico</option>
                </select>
                <div v-if="fechaFilter === 'range'" class="mt-3 flex w-full space-x-2">
                  <datetime
                    class="theme-colescba w-1/2"
                    type="date"
                    input-id="desde"
                    placeholder="Fecha desde"
                    input-class="block h-10 w-full text-sm transition duration-150 ease-in-out"
                    v-model="rangoFechas.desde"
                    zone="America/Argentina/Buenos_Aires"
                    value-zone="America/Argentina/Buenos_Aires"
                    format="dd/MM/yyyy"
                    :phrases="{ ok: 'Aceptar', cancel: 'Cancelar' }"
                    :min-datetime="minDateFrom"
                    :max-datetime="maxDateFrom"
                  />
                  <datetime
                    class="theme-colescba w-1/2"
                    type="date"
                    input-id="hasta"
                    placeholder="Fecha hasta"
                    input-class="block h-10 w-full text-sm transition duration-150 ease-in-out"
                    v-model="rangoFechas.hasta"
                    zone="America/Argentina/Buenos_Aires"
                    value-zone="America/Argentina/Buenos_Aires"
                    format="dd/MM/yyyy"
                    :phrases="{ ok: 'Aceptar', cancel: 'Cancelar' }"
                    :min-datetime="minDateTo"
                    :max-datetime="maxDateTo"
                  />
                </div>
              </div>
              <div class="min-w-0">
                <div class="text-sm font-bold text-gray-500">Estado</div>
                <select
                  class="block h-10 w-full appearance-none truncate rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 sm:text-sm"
                  type="text"
                  v-model="state"
                >
                  <option value="">Todos los estados</option>
                  <option
                    v-for="estado in estadosOrdenados"
                    :key="estado.id"
                    :value="estado.id.trim()"
                  >
                    {{ estado.detalle.mensaje.capitalizeOnlyFirst() }}
                  </option>
                </select>
              </div>
              <div class="min-w-0">
                <div class="text-sm font-bold text-gray-500">Tipo deuda</div>
                <select
                  ref="tipoDeudaSelect"
                  class="block h-10 w-full appearance-none truncate rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 sm:text-sm"
                  type="text"
                  v-model="tipoDeudaId"
                >
                  <option value="">Todos los tipos</option>
                  <option
                    v-for="tipo in tiposDeudaOrdenados"
                    :key="tipo.id"
                    :value="tipo.id.trim()"
                  >
                    {{ tipo.descripcion }}
                  </option>
                </select>
              </div>
            </div>
            <div class="mt-4 flex w-full justify-end gap-3">
              <button
                type="button"
                @click="limpiar"
                class="focus:shadow-outline inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm transition duration-150 ease-in-out hover:bg-cool-gray-200 focus:border-blue-300 focus:outline-none sm:text-sm"
              >
                <span class="text-center text-sm font-bold">Limpiar</span>
              </button>
              <button
                type="button"
                @click="filtrar"
                :disabled="!puedoFiltrar"
                :class="
                  puedoFiltrar
                    ? 'border-blue-brand-700 bg-blue-brand-500 hover:bg-blue-brand-700'
                    : 'cursor-not-allowed border-gray-300 bg-gray-100 opacity-50'
                "
                class="focus:shadow-outline inline-flex justify-center rounded-md border px-4 py-2 text-base font-medium text-white shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:outline-none sm:text-sm"
              >
                <span class="text-center text-sm font-bold">Filtrar</span>
              </button>
            </div>
          </li>
        </template>
      </AccordionItem>
    </Accordion>
  </div>
</template>

<script>
import { useService } from '@colescba/app-vue'

import moment from 'moment'

import Accordion from '@/components/Core/Accordion/Accordion'
import AccordionItem from '@/components/Core/Accordion/AccordionItem'

export default {
  mixins: [useService('portalPagos')],

  components: { Accordion, AccordionItem },

  data() {
    return {
      fechaFilter: 'all',
      state: '',
      tipoDeudaId: '',
      nroComprobante: '',
      isTipoDeudaFocused: false,
      rangoValido: {
        desde: moment().subtract(365, 'd'),
        hasta: moment(),
      },
      rangoFechas: {
        desde: '',
        hasta: '',
      },
      estados: [],
      tiposDeuda: [],
    }
  },

  async mounted() {
    const [estados, tiposDeuda] = await Promise.all([
      this.portalPagos.getEstadosDeuda(),
      this.portalPagos.getTiposDeuda(),
    ])
    this.estados = estados
    this.tiposDeuda = tiposDeuda
  },

  computed: {
    estadosOrdenados() {
      return [...this.estados].sort((a, b) => {
        const aText = (a?.detalle?.mensaje || '').toString().toLowerCase()
        const bText = (b?.detalle?.mensaje || '').toString().toLowerCase()
        return aText.localeCompare(bText, 'es')
      })
    },
    tiposDeudaOrdenados() {
      return [...this.tiposDeuda].sort((a, b) => {
        const aText = (a?.descripcion || '').toString().toLowerCase()
        const bText = (b?.descripcion || '').toString().toLowerCase()
        return aText.localeCompare(bText, 'es')
      })
    },
    minDateFrom() {
      return this.rangoValido.desde.toISOString()
    },
    maxDateFrom() {
      let momentDate = this.rangoFechas.hasta
        ? moment(this.rangoFechas.hasta)
        : this.rangoValido.hasta
      return momentDate.toISOString()
    },
    minDateTo() {
      let momentDate = this.rangoFechas.desde
        ? moment(this.rangoFechas.desde)
        : this.rangoValido.desde
      return momentDate.toISOString()
    },
    maxDateTo() {
      return this.rangoValido.hasta.toISOString()
    },
    nroComprobanteFiltro() {
      return (this.nroComprobante || '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim()
    },
    puedoFiltrar() {
      if (this.fechaFilter === 'range') {
        return Boolean(this.rangoFechas.desde && this.rangoFechas.hasta)
      }
      return true
    },
  },

  methods: {
    onFechaFilterChange() {
      this.rangoFechas.desde = ''
      this.rangoFechas.hasta = ''
    },
    limpiar() {
      this.fechaFilter = 'all'
      this.state = ''
      this.tipoDeudaId = ''
      this.nroComprobante = ''
      this.rangoFechas.desde = ''
      this.rangoFechas.hasta = ''
      this.$emit('change:filter', {})
    },
    buildFiltrosAvanzados() {
      const filtros = {}

      if (this.fechaFilter === 'last-week') {
        filtros.fecha_desde = moment().subtract(7, 'd').format('DD-MM-YYYY')
        filtros.fecha_hasta = moment().format('DD-MM-YYYY')
      } else if (this.fechaFilter === 'last-month') {
        filtros.fecha_desde = moment().subtract(30, 'd').format('DD-MM-YYYY')
        filtros.fecha_hasta = moment().format('DD-MM-YYYY')
      } else if (this.fechaFilter === 'range' && this.rangoFechas.desde && this.rangoFechas.hasta) {
        filtros.fecha_desde = moment(this.rangoFechas.desde).format('DD-MM-YYYY')
        filtros.fecha_hasta = moment(this.rangoFechas.hasta).format('DD-MM-YYYY')
      }

      if (this.state) filtros.estado = this.state
      if (this.tipoDeudaId) filtros.tipo = this.tipoDeudaId
      if (this.nroComprobanteFiltro) filtros.nro_comprobante = this.nroComprobanteFiltro

      return filtros
    },
    filtrar() {
      if (!this.puedoFiltrar) return
      this.$emit('change:filter', this.buildFiltrosAvanzados())
    },
  },
}
</script>
<style scoped>
input[type='date'] {
  display: inline-block;
  position: relative;
}

input[type='date']::-webkit-calendar-picker-indicator {
  background: transparent;
  bottom: 0;
  color: transparent;
  cursor: pointer;
  height: auto;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: auto;
}
</style>
<style>
.vdatetime-popup {
  height: 490px;
  overflow: auto;
}

.vdatetime-popup__actions {
  position: absolute;
  bottom: 0;
  right: 0;
}

.vdatetime-calendar__navigation {
  user-select: none;
}
</style>
